import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import ProgramaAnualPanel from '../components/programas/ProgramaAnualPanel';
import { constanciaSutDelAnio, programasDisponibles } from '../lib/programas/prog-datos';
import { avisoSutAnterior } from '../lib/programas/prog-logica';

// ── Types ──────────────────────────────────────────────────────────────────────

type RecordStatus = 'borrador' | 'vigente';

interface PsychosocialProgram {
  id:                              string;
  company_id:                      string;
  status:                          RecordStatus;
  evaluation_instrument:           string | null;
  evaluation_date:                 string | null;
  action_plan_notes:               string | null;
  sensitization_activities_notes:  string | null;
  registered_sut:                  boolean;
  registered_sut_date:             string | null;
  created_by:                      string | null;
  created_at:                      string;
  updated_at:                      string;
}

interface InternalProtocol {
  id:                          string;
  company_id:                  string;
  status:                      RecordStatus;
  prevention_axis_notes:       string | null;
  detection_axis_notes:        string | null;
  attention_axis_notes:        string | null;
  followup_axis_notes:         string | null;
  registered_mdt:              boolean;
  registered_mdt_date:         string | null;
  approval_resolution_number:  string | null;
  created_by:                  string | null;
  created_at:                  string;
  updated_at:                  string;
}

interface SubstancePreventionProgram {
  id:                              string;
  company_id:                      string;
  status:                          RecordStatus;
  planning_notes:                  string | null;
  responsible_team_notes:          string | null;
  sensitization_activities_notes:  string | null;
  detection_protocol_notes:        string | null;
  referral_network_notes:          string | null;
  registered_sut:                  boolean;
  registered_sut_date:             string | null;
  created_by:                      string | null;
  created_at:                      string;
  updated_at:                      string;
}

type Section = 'programa' | 'protocolo' | 'sustancias';

// ── Constants ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<RecordStatus, string> = {
  borrador: 'Borrador',
  vigente:  'Vigente',
};

function makeEmptyProgramForm() {
  return {
    evaluation_instrument:          '',
    evaluation_date:                '',
    action_plan_notes:              '',
    sensitization_activities_notes: '',
    status:                         'borrador' as RecordStatus,
    registered_sut:                 false,
    registered_sut_date:            '',
  };
}

function makeEmptyProtocolForm() {
  return {
    prevention_axis_notes:      '',
    detection_axis_notes:       '',
    attention_axis_notes:       '',
    followup_axis_notes:        '',
    status:                     'borrador' as RecordStatus,
    registered_mdt:             false,
    registered_mdt_date:        '',
    approval_resolution_number: '',
  };
}

function makeEmptySubstanceForm() {
  return {
    planning_notes:                  '',
    responsible_team_notes:          '',
    sensitization_activities_notes:  '',
    detection_protocol_notes:        '',
    referral_network_notes:          '',
    status:                          'borrador' as RecordStatus,
    registered_sut:                  false,
    registered_sut_date:             '',
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function PsychosocialRiskPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const effectiveCompanyId   = (impersonated ?? company)?.id ?? '';

  const canEdit = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  // Programas anuales (PO 24 a PO 27): versión aprobada y firmada, cronograma, ejecución y registro en el SUT.
  const [programasOk, setProgramasOk] = useState(false);
  useEffect(() => { if (effectiveCompanyId) programasDisponibles(effectiveCompanyId).then(setProgramasOk); }, [effectiveCompanyId]);
  const empresaNombre = (impersonated ?? company)?.name ?? '';
  // Lote C: la casilla anterior "Registrado en SUT" ya no se muestra; si estaba marcada y no hay la constancia nueva del
  // SUT del año (programa anual), se avisa. La columna registered_sut se conserva.
  const [constancias, setConstancias] = useState({ psicosocial: false, sustancias: false });
  useEffect(() => {
    if (!effectiveCompanyId || !programasOk) return;
    const anio = new Date().getFullYear();
    Promise.all([constanciaSutDelAnio(effectiveCompanyId, 'psicosocial', anio), constanciaSutDelAnio(effectiveCompanyId, 'sustancias', anio)])
      .then(([psicosocial, sustancias]) => setConstancias({ psicosocial, sustancias })).catch(() => {});
  }, [effectiveCompanyId, programasOk]);

  const [section, setSection] = useState<Section>('programa');
  const [loading, setLoading] = useState(true);

  // ── Programa de Riesgos Psicosociales ───────────────────────────────────────
  const [program,          setProgram]          = useState<PsychosocialProgram | null>(null);
  const [programEditing,   setProgramEditing]   = useState(false);
  const [programForm,      setProgramForm]      = useState(makeEmptyProgramForm);
  const [programSaving,    setProgramSaving]    = useState(false);
  const [programFormError, setProgramFormError] = useState('');

  // ── Protocolo Interno de Discriminación/Violencia/Acoso ─────────────────────
  const [protocol,          setProtocol]          = useState<InternalProtocol | null>(null);
  const [protocolEditing,   setProtocolEditing]   = useState(false);
  const [protocolForm,      setProtocolForm]      = useState(makeEmptyProtocolForm);
  const [protocolSaving,    setProtocolSaving]    = useState(false);
  const [protocolFormError, setProtocolFormError] = useState('');

  // ── Programa Alcohol/Tabaco/Drogas ───────────────────────────────────────────
  const [substance,          setSubstance]          = useState<SubstancePreventionProgram | null>(null);
  const [substanceEditing,   setSubstanceEditing]   = useState(false);
  const [substanceForm,      setSubstanceForm]      = useState(makeEmptySubstanceForm);
  const [substanceSaving,    setSubstanceSaving]    = useState(false);
  const [substanceFormError, setSubstanceFormError] = useState('');

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    const [{ data: pr }, { data: pc }, { data: sp }] = await Promise.all([
      supabase.from('psychosocial_risk_programs').select('*').eq('company_id', effectiveCompanyId).maybeSingle(),
      supabase.from('internal_protocol_discrimination').select('*').eq('company_id', effectiveCompanyId).maybeSingle(),
      supabase.from('substance_prevention_programs').select('*').eq('company_id', effectiveCompanyId).maybeSingle(),
    ]);
    setProgram((pr ?? null) as PsychosocialProgram | null);
    setProtocol((pc ?? null) as InternalProtocol | null);
    setSubstance((sp ?? null) as SubstancePreventionProgram | null);
    setLoading(false);
  }, [effectiveCompanyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Programa handlers ────────────────────────────────────────────────────────

  function openProgramForm() {
    if (program) {
      setProgramForm({
        evaluation_instrument:          program.evaluation_instrument ?? '',
        evaluation_date:                program.evaluation_date ?? '',
        action_plan_notes:              program.action_plan_notes ?? '',
        sensitization_activities_notes: program.sensitization_activities_notes ?? '',
        status:                         program.status,
        registered_sut:                 program.registered_sut,
        registered_sut_date:            program.registered_sut_date ?? '',
      });
    } else {
      setProgramForm(makeEmptyProgramForm());
    }
    setProgramFormError('');
    setProgramEditing(true);
  }

  async function handleSaveProgram() {
    setProgramSaving(true);
    setProgramFormError('');

    const payload = {
      company_id:                      effectiveCompanyId,
      status:                          programForm.status,
      evaluation_instrument:           programForm.evaluation_instrument.trim() || null,
      evaluation_date:                 programForm.evaluation_date || null,
      action_plan_notes:               programForm.action_plan_notes.trim() || null,
      sensitization_activities_notes:  programForm.sensitization_activities_notes.trim() || null,
      registered_sut:                  programForm.registered_sut,
      registered_sut_date:             programForm.registered_sut ? (programForm.registered_sut_date || null) : null,
      updated_at:                      new Date().toISOString(),
      ...(program ? {} : { created_by: profile?.id ?? null }),
    };

    const { error } = await supabase
      .from('psychosocial_risk_programs')
      .upsert(payload, { onConflict: 'company_id' });

    if (error) {
      setProgramFormError('Error al guardar el programa: ' + error.message);
      setProgramSaving(false);
      return;
    }

    setProgramSaving(false);
    setProgramEditing(false);
    await loadData();
  }

  // ── Protocolo handlers ───────────────────────────────────────────────────────

  function openProtocolForm() {
    if (protocol) {
      setProtocolForm({
        prevention_axis_notes:      protocol.prevention_axis_notes ?? '',
        detection_axis_notes:       protocol.detection_axis_notes ?? '',
        attention_axis_notes:       protocol.attention_axis_notes ?? '',
        followup_axis_notes:        protocol.followup_axis_notes ?? '',
        status:                     protocol.status,
        registered_mdt:             protocol.registered_mdt,
        registered_mdt_date:        protocol.registered_mdt_date ?? '',
        approval_resolution_number: protocol.approval_resolution_number ?? '',
      });
    } else {
      setProtocolForm(makeEmptyProtocolForm());
    }
    setProtocolFormError('');
    setProtocolEditing(true);
  }

  async function handleSaveProtocol() {
    setProtocolSaving(true);
    setProtocolFormError('');

    const payload = {
      company_id:                  effectiveCompanyId,
      status:                      protocolForm.status,
      prevention_axis_notes:       protocolForm.prevention_axis_notes.trim() || null,
      detection_axis_notes:        protocolForm.detection_axis_notes.trim() || null,
      attention_axis_notes:        protocolForm.attention_axis_notes.trim() || null,
      followup_axis_notes:         protocolForm.followup_axis_notes.trim() || null,
      registered_mdt:              protocolForm.registered_mdt,
      registered_mdt_date:         protocolForm.registered_mdt ? (protocolForm.registered_mdt_date || null) : null,
      approval_resolution_number:  protocolForm.registered_mdt ? (protocolForm.approval_resolution_number.trim() || null) : null,
      updated_at:                  new Date().toISOString(),
      ...(protocol ? {} : { created_by: profile?.id ?? null }),
    };

    const { error } = await supabase
      .from('internal_protocol_discrimination')
      .upsert(payload, { onConflict: 'company_id' });

    if (error) {
      setProtocolFormError('Error al guardar el protocolo: ' + error.message);
      setProtocolSaving(false);
      return;
    }

    setProtocolSaving(false);
    setProtocolEditing(false);
    await loadData();
  }

  // ── Programa Alcohol/Tabaco/Drogas handlers ─────────────────────────────────

  function openSubstanceForm() {
    if (substance) {
      setSubstanceForm({
        planning_notes:                  substance.planning_notes ?? '',
        responsible_team_notes:          substance.responsible_team_notes ?? '',
        sensitization_activities_notes:  substance.sensitization_activities_notes ?? '',
        detection_protocol_notes:        substance.detection_protocol_notes ?? '',
        referral_network_notes:          substance.referral_network_notes ?? '',
        status:                          substance.status,
        registered_sut:                  substance.registered_sut,
        registered_sut_date:             substance.registered_sut_date ?? '',
      });
    } else {
      setSubstanceForm(makeEmptySubstanceForm());
    }
    setSubstanceFormError('');
    setSubstanceEditing(true);
  }

  async function handleSaveSubstance() {
    setSubstanceSaving(true);
    setSubstanceFormError('');

    const payload = {
      company_id:                      effectiveCompanyId,
      status:                          substanceForm.status,
      planning_notes:                  substanceForm.planning_notes.trim() || null,
      responsible_team_notes:          substanceForm.responsible_team_notes.trim() || null,
      sensitization_activities_notes:  substanceForm.sensitization_activities_notes.trim() || null,
      detection_protocol_notes:        substanceForm.detection_protocol_notes.trim() || null,
      referral_network_notes:          substanceForm.referral_network_notes.trim() || null,
      registered_sut:                  substanceForm.registered_sut,
      registered_sut_date:             substanceForm.registered_sut ? (substanceForm.registered_sut_date || null) : null,
      updated_at:                      new Date().toISOString(),
      ...(substance ? {} : { created_by: profile?.id ?? null }),
    };

    const { error } = await supabase
      .from('substance_prevention_programs')
      .upsert(payload, { onConflict: 'company_id' });

    if (error) {
      setSubstanceFormError('Error al guardar el programa: ' + error.message);
      setSubstanceSaving(false);
      return;
    }

    setSubstanceSaving(false);
    setSubstanceEditing(false);
    await loadData();
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Riesgo Psicosocial y Prevención de Sustancias</h2>
        <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
          Programa de prevención de riesgo psicosocial, Protocolo Interno de Discriminación/Violencia/Acoso y Programa de Prevención de Alcohol, Tabaco y Drogas — MDT-2025-102 (reformado por MDT-2025-186) y Acuerdo Interministerial MDT-MSP-2019-038
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #334155', flexWrap: 'wrap' }}>
        {([
          { id: 'programa',   label: 'Programa de Riesgos Psicosociales' },
          { id: 'protocolo',  label: 'Protocolo Interno de Discriminación/Violencia/Acoso' },
          { id: 'sustancias', label: 'Alcohol/Tabaco/Drogas' },
        ] as { id: Section; label: string }[]).map(t => (
          <button
            key={t.id}
            onClick={() => setSection(t.id)}
            style={{
              ...tabBtnStyle,
              ...(section === t.id ? tabBtnActiveStyle : {}),
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
      ) : (
        <>
          {/* ── SECCIÓN 1: Programa de Riesgos Psicosociales ───────────────── */}
          {section === 'programa' && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: 20 }}>
              {!programEditing ? (
                program ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={statusBadgeStyle(program.status)}>{STATUS_LABELS[program.status]}</span>
                      {canEdit && <button onClick={openProgramForm} style={ghostBtn}>Editar programa</button>}
                    </div>
                    <InfoField label="Instrumento de evaluación" value={program.evaluation_instrument} />
                    <InfoField label="Fecha de evaluación" value={program.evaluation_date ? new Date(program.evaluation_date + 'T00:00:00').toLocaleDateString('es-EC') : null} />
                    <InfoField label="Plan de acción" value={program.action_plan_notes} />
                    <InfoField label="Actividades de sensibilización" value={program.sensitization_activities_notes} />
                    {avisoSutAnterior(program.registered_sut, constancias.psicosocial) && (
                      <p role="status" style={{ color: '#fbbf24', fontSize: '0.84rem', margin: 0 }}>{avisoSutAnterior(program.registered_sut, constancias.psicosocial)}</p>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 16px' }}>
                      Esta empresa aún no tiene un Programa de Riesgos Psicosociales registrado.
                    </p>
                    {canEdit && (
                      <button onClick={openProgramForm} style={primaryBtn}>+ Crear Programa</button>
                    )}
                  </div>
                )
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Instrumento de evaluación">
                    <input style={inp} value={programForm.evaluation_instrument}
                      onChange={e => setProgramForm(f => ({ ...f, evaluation_instrument: e.target.value }))}
                      placeholder="Ej: Cuestionario CoPsoQ-Istas21" />
                  </Field>
                  <Field label="Fecha de evaluación">
                    <input style={inp} type="date" value={programForm.evaluation_date}
                      onChange={e => setProgramForm(f => ({ ...f, evaluation_date: e.target.value }))} />
                  </Field>
                  <Field label="Plan de acción">
                    <textarea style={{ ...ta, minHeight: 70 }} value={programForm.action_plan_notes}
                      onChange={e => setProgramForm(f => ({ ...f, action_plan_notes: e.target.value }))}
                      placeholder="Medidas de intervención derivadas de la evaluación…" />
                  </Field>
                  <Field label="Actividades de sensibilización">
                    <textarea style={{ ...ta, minHeight: 70 }} value={programForm.sensitization_activities_notes}
                      onChange={e => setProgramForm(f => ({ ...f, sensitization_activities_notes: e.target.value }))}
                      placeholder="Capacitaciones, campañas y talleres de sensibilización…" />
                  </Field>
                  <Field label="Estado">
                    <select style={sel} value={programForm.status}
                      onChange={e => setProgramForm(f => ({ ...f, status: e.target.value as RecordStatus }))}>
                      {(Object.keys(STATUS_LABELS) as RecordStatus[]).map(s =>
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      )}
                    </select>
                  </Field>
                  {programFormError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>{programFormError}</p>}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setProgramEditing(false)} style={cancelBtn} disabled={programSaving}>Cancelar</button>
                    <button onClick={handleSaveProgram} disabled={programSaving} style={{ ...saveBtn, opacity: programSaving ? 0.7 : 1 }}>
                      {programSaving ? 'Guardando…' : 'Guardar programa'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {section === 'programa' && programasOk && (
            <div style={{ marginTop: 16 }}>
              <ProgramaAnualPanel tipo="psicosocial" companyId={effectiveCompanyId} empresa={empresaNombre} puedeEditar={canEdit} />
            </div>
          )}

          {/* ── SECCIÓN 2: Protocolo Interno de Discriminación/Violencia/Acoso ── */}
          {section === 'protocolo' && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: 20 }}>
              {!protocolEditing ? (
                protocol ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={statusBadgeStyle(protocol.status)}>{STATUS_LABELS[protocol.status]}</span>
                      {canEdit && <button onClick={openProtocolForm} style={ghostBtn}>Editar protocolo</button>}
                    </div>
                    <InfoField label="Eje 1 — Prevención" value={protocol.prevention_axis_notes} />
                    <InfoField label="Eje 2 — Identificación y Detección" value={protocol.detection_axis_notes} />
                    <InfoField label="Eje 3 — Atención" value={protocol.attention_axis_notes} />
                    <InfoField label="Eje 4 — Seguimiento y Evaluación" value={protocol.followup_axis_notes} />
                    <InfoField
                      label="Registrado en plataforma MDT"
                      value={protocol.registered_mdt
                        ? `Sí${protocol.registered_mdt_date ? ' — ' + new Date(protocol.registered_mdt_date + 'T00:00:00').toLocaleDateString('es-EC') : ''}${protocol.approval_resolution_number ? ` (Resolución ${protocol.approval_resolution_number})` : ''}`
                        : 'No'}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 16px' }}>
                      Esta empresa aún no tiene un Protocolo Interno de Discriminación, Violencia y Acoso registrado.
                    </p>
                    {canEdit && (
                      <button onClick={openProtocolForm} style={primaryBtn}>+ Crear Protocolo</button>
                    )}
                  </div>
                )
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Eje 1 — Prevención">
                    <textarea style={{ ...ta, minHeight: 70 }} value={protocolForm.prevention_axis_notes}
                      onChange={e => setProtocolForm(f => ({ ...f, prevention_axis_notes: e.target.value }))}
                      placeholder="Medidas preventivas de discriminación, violencia y acoso laboral…" />
                  </Field>
                  <Field label="Eje 2 — Identificación y Detección">
                    <textarea style={{ ...ta, minHeight: 70 }} value={protocolForm.detection_axis_notes}
                      onChange={e => setProtocolForm(f => ({ ...f, detection_axis_notes: e.target.value }))}
                      placeholder="Mecanismos de identificación y detección de casos…" />
                  </Field>
                  <Field label="Eje 3 — Atención">
                    <textarea style={{ ...ta, minHeight: 70 }} value={protocolForm.attention_axis_notes}
                      onChange={e => setProtocolForm(f => ({ ...f, attention_axis_notes: e.target.value }))}
                      placeholder="Procedimiento de atención y canalización de denuncias…" />
                  </Field>
                  <Field label="Eje 4 — Seguimiento y Evaluación">
                    <textarea style={{ ...ta, minHeight: 70 }} value={protocolForm.followup_axis_notes}
                      onChange={e => setProtocolForm(f => ({ ...f, followup_axis_notes: e.target.value }))}
                      placeholder="Mecanismos de seguimiento y evaluación de casos atendidos…" />
                  </Field>
                  <Field label="Estado">
                    <select style={sel} value={protocolForm.status}
                      onChange={e => setProtocolForm(f => ({ ...f, status: e.target.value as RecordStatus }))}>
                      {(Object.keys(STATUS_LABELS) as RecordStatus[]).map(s =>
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      )}
                    </select>
                  </Field>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={protocolForm.registered_mdt}
                      onChange={e => setProtocolForm(f => ({ ...f, registered_mdt: e.target.checked }))}
                      style={{ width: 16, height: 16, accentColor: '#2FBF71', cursor: 'pointer' }}
                    />
                    <span style={{ color: '#cbd5e1', fontSize: '0.86rem' }}>Registrado en plataforma MDT</span>
                  </label>
                  {protocolForm.registered_mdt && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Field label="Fecha de registro en MDT">
                        <input style={inp} type="date" value={protocolForm.registered_mdt_date}
                          onChange={e => setProtocolForm(f => ({ ...f, registered_mdt_date: e.target.value }))} />
                      </Field>
                      <Field label="Número de resolución de aprobación">
                        <input style={inp} value={protocolForm.approval_resolution_number}
                          onChange={e => setProtocolForm(f => ({ ...f, approval_resolution_number: e.target.value }))}
                          placeholder="Ej: MDT-2026-0123" />
                      </Field>
                    </div>
                  )}
                  {protocolFormError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>{protocolFormError}</p>}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setProtocolEditing(false)} style={cancelBtn} disabled={protocolSaving}>Cancelar</button>
                    <button onClick={handleSaveProtocol} disabled={protocolSaving} style={{ ...saveBtn, opacity: protocolSaving ? 0.7 : 1 }}>
                      {protocolSaving ? 'Guardando…' : 'Guardar protocolo'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SECCIÓN 3: Programa Alcohol/Tabaco/Drogas ──────────────────── */}
          {section === 'sustancias' && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: 20 }}>
              {!substanceEditing ? (
                substance ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={statusBadgeStyle(substance.status)}>{STATUS_LABELS[substance.status]}</span>
                      {canEdit && <button onClick={openSubstanceForm} style={ghostBtn}>Editar programa</button>}
                    </div>
                    <InfoField label="Planificación anual" value={substance.planning_notes} />
                    <InfoField label="Equipo multidisciplinario responsable" value={substance.responsible_team_notes} />
                    <InfoField label="Actividades de sensibilización" value={substance.sensitization_activities_notes} />
                    <InfoField label="Protocolo de detección" value={substance.detection_protocol_notes} />
                    <InfoField label="Red de derivación / atención especializada" value={substance.referral_network_notes} />
                    {avisoSutAnterior(substance.registered_sut, constancias.sustancias) && (
                      <p role="status" style={{ color: '#fbbf24', fontSize: '0.84rem', margin: 0 }}>{avisoSutAnterior(substance.registered_sut, constancias.sustancias)}</p>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: 24 }}>
                    <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 16px' }}>
                      Esta empresa aún no tiene un Programa de Prevención de Alcohol, Tabaco y Drogas registrado.
                    </p>
                    {canEdit && (
                      <button onClick={openSubstanceForm} style={primaryBtn}>+ Crear Programa</button>
                    )}
                  </div>
                )
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Planificación anual">
                    <textarea style={{ ...ta, minHeight: 70 }} value={substanceForm.planning_notes}
                      onChange={e => setSubstanceForm(f => ({ ...f, planning_notes: e.target.value }))}
                      placeholder="Planificación anual del programa de prevención…" />
                  </Field>
                  <Field label="Equipo multidisciplinario responsable">
                    <textarea style={{ ...ta, minHeight: 70 }} value={substanceForm.responsible_team_notes}
                      onChange={e => setSubstanceForm(f => ({ ...f, responsible_team_notes: e.target.value }))}
                      placeholder="Integrantes y roles del equipo responsable del programa…" />
                  </Field>
                  <Field label="Actividades de sensibilización">
                    <textarea style={{ ...ta, minHeight: 70 }} value={substanceForm.sensitization_activities_notes}
                      onChange={e => setSubstanceForm(f => ({ ...f, sensitization_activities_notes: e.target.value }))}
                      placeholder="Capacitaciones, campañas y talleres de sensibilización…" />
                  </Field>
                  <Field label="Protocolo de detección">
                    <textarea style={{ ...ta, minHeight: 70 }} value={substanceForm.detection_protocol_notes}
                      onChange={e => setSubstanceForm(f => ({ ...f, detection_protocol_notes: e.target.value }))}
                      placeholder="Mecanismos de detección temprana de consumo…" />
                  </Field>
                  <Field label="Red de derivación / atención especializada">
                    <textarea style={{ ...ta, minHeight: 70 }} value={substanceForm.referral_network_notes}
                      onChange={e => setSubstanceForm(f => ({ ...f, referral_network_notes: e.target.value }))}
                      placeholder="Convenios y contactos de derivación a atención especializada…" />
                  </Field>
                  <Field label="Estado">
                    <select style={sel} value={substanceForm.status}
                      onChange={e => setSubstanceForm(f => ({ ...f, status: e.target.value as RecordStatus }))}>
                      {(Object.keys(STATUS_LABELS) as RecordStatus[]).map(s =>
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                      )}
                    </select>
                  </Field>
                  {substanceFormError && <p style={{ color: '#ef4444', fontSize: '0.82rem', margin: 0 }}>{substanceFormError}</p>}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={() => setSubstanceEditing(false)} style={cancelBtn} disabled={substanceSaving}>Cancelar</button>
                    <button onClick={handleSaveSubstance} disabled={substanceSaving} style={{ ...saveBtn, opacity: substanceSaving ? 0.7 : 1 }}>
                      {substanceSaving ? 'Guardando…' : 'Guardar programa'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {section === 'sustancias' && programasOk && (
            <div style={{ marginTop: 16 }}>
              <ProgramaAnualPanel tipo="sustancias" companyId={effectiveCompanyId} empresa={empresaNombre} puedeEditar={canEdit} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>{label}</span>
      <span style={{ color: '#cbd5e1', fontSize: '0.86rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{value || '—'}</span>
    </div>
  );
}

function statusBadgeStyle(status: RecordStatus): React.CSSProperties {
  const palette = status === 'vigente'
    ? { bg: 'rgba(47,191,113,0.12)', color: '#2FBF71', border: 'rgba(47,191,113,0.3)' }
    : { bg: 'rgba(234,179,8,0.12)',  color: '#eab308', border: 'rgba(234,179,8,0.3)' };
  return {
    background: palette.bg, color: palette.color, border: `1px solid ${palette.border}`,
    borderRadius: 6, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700,
  };
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn:  React.CSSProperties = { padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
const ta:          React.CSSProperties = { ...inp, resize: 'vertical' };
const cancelBtn:   React.CSSProperties = { padding: '9px 20px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 };
const saveBtn:     React.CSSProperties = { padding: '9px 24px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700 };
const tabBtnStyle: React.CSSProperties = { padding: '10px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#64748b', fontSize: '0.86rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' };
const tabBtnActiveStyle: React.CSSProperties = { color: '#2FBF71', borderBottomColor: '#2FBF71' };
